import { z } from "zod";
import { createTRPCRouter, protectedProcedure, publicProcedure } from "../trpc";
import { $Enums, UserRole } from "@prisma/client";
import { TRPCError } from "@trpc/server";

const userData = {
    id: true,
    name: true,
    email: true,
    role: true,
    verified: true,
    image: true
};

export const userRouter = createTRPCRouter({
    contextUser: publicProcedure.query(({ ctx }) => {
        if (!ctx.contextUser) {
            throw new TRPCError({ code: "UNAUTHORIZED", message: "User not authenticated" });
        }
        return ctx.contextUser
    }),

    googleLogin: publicProcedure
        .input(
            z.object({
                name: z.string(),
                email: z.string().email(),
                image: z.string().optional(),
            })
        )
        .mutation(async ({ input, ctx }) => {
            const existingUser = await ctx.prisma.user.findUnique({
                where: { email: input.email }
            });

            if (existingUser) {
                return existingUser;
            }

            // Create new user
            const newUser = await ctx.prisma.user.create({
                data: {
                    name: input.name,
                    email: input.email,
                    image: input.image,
                    role: UserRole.VIEWER,
                },
            });

            return newUser;
        }),

    verifyEmail: protectedProcedure
        .input(z.object({ userId: z.string() }))
        .mutation(async ({ ctx, input }) => {
            const currentUser = await ctx.prisma.user.findUnique({
                where: { id: ctx.user.id },
            })

            if (currentUser?.role !== "ADMIN") {
                throw new TRPCError({
                    code: "FORBIDDEN",
                    message: "Only admins can verify emails",
                })
            }

            try {
                await ctx.prisma.user.update({
                    where: { id: input.userId },
                    data: { verified: true },
                })

                return { success: true }
            } catch (error) {
                console.error("Error verifying email:", error)
                throw new TRPCError({
                    code: "INTERNAL_SERVER_ERROR",
                    message: "Failed to verify user email",
                })
            }
        }),

    getAll: protectedProcedure
        .query(async ({ ctx }) => {
            // Check if current user is admin
            const currentUser = await ctx.prisma.user.findUnique({
                where: { id: ctx.user.id },
            })

            if (currentUser?.role !== "ADMIN") {
                throw new TRPCError({
                    code: "FORBIDDEN",
                    message: "Only admins can view all users",
                })
            }

            return ctx.prisma.user.findMany({
                select: {
                    id: true,
                    email: true,
                    verified: true,
                    role: true,
                },
                orderBy: { createdAt: "desc" },
            })
        }),

    updateRole: protectedProcedure
        .input(z.object({
            userId: z.string(),
            role: z.nativeEnum(UserRole)
        }))
        .mutation(async ({ ctx, input }) => {
            // Check if current user is admin
            const currentUser = await ctx.prisma.user.findUnique({
                where: { id: ctx.user.id },
            })

            if (currentUser?.role !== "ADMIN") {
                throw new TRPCError({
                    code: "FORBIDDEN",
                    message: "Only admins can update user roles",
                })
            }

            try {
                await ctx.prisma.user.update({
                    where: { id: input.userId },
                    data: { role: input.role, verified: true },
                })

                return { success: true }
            } catch (error) {
                console.error("Error updating user role:", error)
                throw new TRPCError({
                    code: "INTERNAL_SERVER_ERROR",
                    message: "Failed to update user role",
                })
            }
        }),

    deleteUser: protectedProcedure
        .input(z.object({ userId: z.string() }))
        .mutation(async ({ ctx, input }) => {
            // Check if current user is admin
            const currentUser = await ctx.prisma.user.findUnique({
                where: { id: ctx.user.id },
            })

            if (currentUser?.role !== "ADMIN") {
                throw new TRPCError({
                    code: "FORBIDDEN",
                    message: "Only admins can delete users",
                })
            }

            // Don't allow admins to delete themselves
            if (input.userId === ctx.user.id) {
                throw new TRPCError({
                    code: "BAD_REQUEST",
                    message: "You cannot delete your own account",
                })
            }

            try {
                await ctx.prisma.user.delete({
                    where: { id: input.userId },
                })

                return { success: true }
            } catch (error) {
                console.error("Error deleting user:", error)
                throw new TRPCError({
                    code: "INTERNAL_SERVER_ERROR",
                    message: "Failed to delete user",
                })
            }
        }),

    getWaitingList: protectedProcedure
        .query(async ({ ctx }) => {
            // Check if current user is admin
            const currentUser = await ctx.prisma.user.findUnique({
                where: { id: ctx.user.id },
            })

            if (currentUser?.role !== "ADMIN") {
                throw new TRPCError({
                    code: "FORBIDDEN",
                    message: "Only admins can view all users",
                })
            }

            const waitingList = await ctx.prisma.user.count({
                where: {
                    verified: false
                }
            })
            return waitingList;
        }),
})
